import { Link, useParams } from "react-router-dom";

import { Pill, StatusBadge } from "../components/Badges";
import { BarList } from "../components/BarList";
import { DataTable } from "../components/DataTable";
import { IssueList } from "../components/IssueList";
import { MonthBars } from "../components/MonthBars";
import { Card, Empty, ErrorBox, Facts, Loading, PageHeader } from "../components/Page";
import { Tabs, useTab } from "../components/Tabs";
import type { DocStatus, Issue } from "../lib/api";
import { day, money, num, titleCase } from "../lib/format";
import { useApi } from "../lib/useApi";
import { StockTable, type StockRow, type TransitRow } from "./Stock";

interface ItemRow {
  sku: string; name: string; flavour: string | null; size_ml: number | null; units_per_case: number | null; proof: number | null;
  product_class: string; on_hand_cases: number | null; on_hand_qty: number | null; sold_cases: number | null; aliases: number;
}

const CLASS_TABS = [
  { id: "finished", label: "Finished goods" },
  { id: "material", label: "Packaging" },
  { id: "bulk", label: "Bulk liquid" },
  { id: "merch", label: "Merchandise" },
];

export function Products() {
  const { data, error, reload } = useApi<ItemRow[]>("/items");
  const tabs = CLASS_TABS.map((t) => ({ ...t, count: data?.filter((i) => i.product_class === t.id).length }));
  const [tab, setTab] = useTab(tabs, "class");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  const rows = (data ?? []).filter((i) => i.product_class === tab);
  return (
    <>
      <PageHeader title="Products" sub="One record per real product, whatever each warehouse calls it. Packaging and bulk come from BCI's own codes." />
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      <Card>
        {!data ? <Loading /> : (
          <DataTable<ItemRow> testId="products-table" rows={rows} rowKey={(r) => r.sku} href={(r) => `/products/${r.sku}`}
            columns={[
              { key: "name", label: "Product", render: (r) => <Link to={`/products/${r.sku}`}>{r.name}</Link> },
              { key: "sku", label: "SKU", render: (r) => <code>{r.sku}</code> },
              ...(tab === "finished" ? [
                { key: "size_ml", label: "Size", numeric: true, render: (r: ItemRow) => (r.size_ml ? `${r.size_ml}ml × ${r.units_per_case}` : "—") },
                { key: "on_hand_cases", label: "On hand (cs)", numeric: true, render: (r: ItemRow) => num(r.on_hand_cases, 2) },
                { key: "sold_cases", label: "Sold YTD (cs)", numeric: true, render: (r: ItemRow) => num(r.sold_cases) },
              ] : [
                { key: "on_hand_qty", label: "On hand", numeric: true, render: (r: ItemRow) => num(r.on_hand_qty, 2) },
              ]),
              { key: "aliases", label: "Names in sources", numeric: true },
            ]} />
        )}
      </Card>
    </>
  );
}

interface ItemDetail {
  sku: string; name: string; flavour: string | null; size_ml: number | null; units_per_case: number | null; proof: number | null; product_class: string;
  aliases: { source: string; source_name: string; code: string | null; text: string | null; confirmed: boolean; notes: string | null }[];
  stock: StockRow[]; in_transit: TransitRow[];
  history: { as_of: string; location: string; qty: number; uom: string }[];
  sales_monthly: { month: string; cases: number }[];
  sales_by_channel: { code: string; name: string; cases: number }[];
  top_customers: { id: number; name: string; cases: number }[];
  documents: { id: number; doc_type: string; number: string; status: DocStatus; date: string | null; qty: number; unit_price: number }[];
  costs: { location: string | null; as_of: string; unit_cost: number; currency: string }[];
  issues: { id: number; severity: Issue["severity"]; summary: string }[];
  suppliers: { vendor: string; vendor_id: number | null; code: string | null; unit_price: number;
               currency: string; uom: string | null; minimum_order: number | null;
               lead_time_days: number | null; as_of: string; notes: string | null }[];
}

export function ProductDetail() {
  const { sku } = useParams();
  const { data, error, reload } = useApi<ItemDetail>(`/items/${sku}`);
  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "stock", label: "Stock", count: data?.stock.length },
    { id: "sales", label: "Sales" },
    { id: "names", label: "Names in sources", count: data?.aliases.length },
    { id: "documents", label: "Documents", count: data?.documents.length },
    { id: "costs", label: "Costs", count: data?.costs.length },
    ...(data?.suppliers.length ? [{ id: "suppliers", label: "Suppliers", count: data.suppliers.length }] : []),
  ];
  const [tab, setTab] = useTab(tabs);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const onHand = data.stock.reduce((s, r) => s + (r.cases ?? 0), 0);
  const sold = data.sales_by_channel.reduce((s, r) => s + r.cases, 0);

  return (
    <>
      <PageHeader crumbs={[{ label: "Products", to: "/products" }]} title={data.name}
        badge={<Pill>{titleCase(data.product_class)}</Pill>} sub={<code>{data.sku}</code>} />
      <Tabs tabs={tabs} current={tab} onChange={setTab} />

      {tab === "overview" && (
        <>
          <div className="two-up">
            <Card title="Product">
              <Facts items={[["SKU", <code>{data.sku}</code>], ["Flavour", data.flavour], ["Size", data.size_ml ? `${data.size_ml} ml` : null],
                ["Units per case", num(data.units_per_case)], ["Proof", data.proof ? `${data.proof} (${data.proof / 2}% ABV)` : null],
                ["On hand", data.product_class === "finished" ? `${num(onHand, 2)} cases` : null],
                ["In transit", data.in_transit.length ? `${num(data.in_transit.reduce((s, t) => s + t.cases, 0))} cases` : "none"],
                ["Sold YTD", data.product_class === "finished" ? `${num(sold)} cases` : null]]} />
            </Card>
            <Card title="Open issues">
              <IssueList issues={data.issues.map((i) => ({ ...i, check: "", detail: {}, detected_at: "", resolved_at: null, source: null, run_id: null }))} compact />
            </Card>
          </div>
          {data.sales_monthly.length > 0 && (
            <Card title="Cases sold by month">
              <MonthBars title="Cases" points={data.sales_monthly.map((m) => ({ month: m.month, value: m.cases, label: `${num(m.cases)} cs` }))} />
            </Card>
          )}
        </>
      )}

      {tab === "stock" && (
        <>
          <Card title="Latest by location"><StockTable rows={data.stock} /></Card>
          {data.in_transit.length > 0 && (
            <Card title="In transit">
              <DataTable rows={data.in_transit} rowKey={(t) => t.document_id} href={(t) => `/documents/${t.document_id}`} filterPlaceholder={false}
                columns={[{ key: "number", label: "Transfer", render: (t) => `#${t.number}` }, { key: "cases", label: "Cases", numeric: true },
                  { key: "route", label: "Route", value: (t) => `${t.from} → ${t.to}` }, { key: "shipped", label: "Shipped", render: (t) => day(t.shipped) }]} />
            </Card>
          )}
          <Card title="History (every snapshot)">
            <DataTable rows={data.history} rowKey={(h) => `${h.as_of}-${h.location}`} filterPlaceholder={false}
              initialSort={{ key: "as_of", dir: "desc" }} columns={[
                { key: "as_of", label: "As of", render: (h) => day(h.as_of) },
                { key: "location", label: "Location", render: (h) => <Link to={`/locations/${h.location}`}>{h.location}</Link> },
                { key: "qty", label: "Quantity", numeric: true, render: (h) => num(h.qty, 2) },
                { key: "uom", label: "Unit" },
              ]} />
          </Card>
        </>
      )}

      {tab === "sales" && (
        <div className="two-up">
          <Card title="Cases by channel">
            {data.sales_by_channel.length ? <BarList bars={data.sales_by_channel.map((c) => ({ key: c.code, label: c.name, value: c.cases, valueLabel: `${num(c.cases)} cs` }))} /> : <Empty>No sales recorded.</Empty>}
          </Card>
          <Card title="Top customers">
            {data.top_customers.length ? <BarList bars={data.top_customers.map((c) => ({ key: String(c.id), label: c.name, value: c.cases, valueLabel: `${num(c.cases)} cs`, href: `/customers/${c.id}` }))} /> : <Empty>No sales recorded.</Empty>}
          </Card>
        </div>
      )}

      {tab === "names" && (
        <Card title="What each source calls this product">
          <DataTable rows={data.aliases} rowKey={(a) => `${a.source}-${a.code}-${a.text}`} filterPlaceholder={false} columns={[
            { key: "source_name", label: "Source" },
            { key: "code", label: "Code", render: (a) => (a.code ? <code>{a.code}</code> : "—") },
            { key: "text", label: "Description", render: (a) => a.text ?? "—" },
            { key: "confirmed", label: "Status", render: (a) => (a.confirmed ? <Pill tone="good">confirmed</Pill> : <Pill tone="warning">inferred — ask Zach</Pill>) },
            { key: "notes", label: "Notes", render: (a) => a.notes ?? "" },
          ]} />
        </Card>
      )}

      {tab === "documents" && (
        <Card>
          <DataTable rows={data.documents} rowKey={(d) => `${d.id}-${d.qty}`} href={(d) => `/documents/${d.id}`} filterPlaceholder={false}
            empty="Not on any document yet." columns={[
              { key: "number", label: "Number", render: (d) => <Link to={`/documents/${d.id}`}>{d.number}</Link> }, { key: "doc_type", label: "Type", render: (d) => titleCase(d.doc_type) },
              { key: "status", label: "Status", render: (d) => <StatusBadge status={d.status} docType={d.doc_type} /> },
              { key: "date", label: "Date", render: (d) => day(d.date) },
              { key: "qty", label: "Qty", numeric: true, render: (d) => num(d.qty) },
              { key: "unit_price", label: "Unit price", numeric: true, render: (d) => money(d.unit_price) },
            ]} />
        </Card>
      )}

      {tab === "suppliers" && (
        <Card title="Who supplies this, and what they charge">
          <DataTable rows={data.suppliers} rowKey={(v) => v.vendor} filterPlaceholder={false}
            initialSort={{ key: "unit_price", dir: "asc" }} columns={[
              { key: "vendor", label: "Supplier", render: (v) => (v.vendor_id
                ? <Link to={`/settings/address-book/${v.vendor_id}`}>{v.vendor}</Link> : v.vendor) },
              { key: "code", label: "Their code", render: (v) => (v.code ? <code>{v.code}</code> : "—") },
              { key: "unit_price", label: "Unit price", numeric: true,
                render: (v) => `${money(v.unit_price, v.currency, 5)}${v.uom ? ` / ${v.uom}` : ""}` },
              { key: "minimum_order", label: "Minimum", numeric: true, render: (v) => num(v.minimum_order) },
              { key: "lead_time_days", label: "Lead time", render: (v) => (v.lead_time_days ? `${v.lead_time_days} days` : "—") },
              { key: "as_of", label: "Quoted", render: (v) => day(v.as_of) },
              { key: "notes", label: "Notes", render: (v) => v.notes ?? "" },
            ]} />
          <p className="note muted">The newest price from each supplier. Upload a price list on the Integrations page to update these.</p>
        </Card>
      )}

      {tab === "costs" && (
        <Card title="Unit cost by location (from the inventory workbooks)">
          <DataTable rows={data.costs} rowKey={(c) => `${c.location}-${c.as_of}`} filterPlaceholder={false} empty="No costs recorded."
            columns={[{ key: "as_of", label: "As of", render: (c) => day(c.as_of) }, { key: "location", label: "Location" },
              { key: "unit_cost", label: "Unit cost", numeric: true, render: (c) => money(c.unit_cost, c.currency) }]} />
        </Card>
      )}
    </>
  );
}
